import React, { useState } from "react";
import {
  Users,
  FileText,
  RotateCcw,
  HandCoins,
  ReceiptText,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Tab } from "../Tab/Tab";
import { ICseDashboardTab } from "../../pages/EPCCustomerManager/CseDashboard";
import { IStatusCycle } from "../../models/commissionAndRepayment.model";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

interface NavigationHeaderProps {
  activeTab: ICseDashboardTab;
  onTabChange: (tab: ICseDashboardTab) => void;
  repaymentStatus?: string;
  setRepaymentStatus?: (status: string) => void;
}

const NavigationHeaderEPC: React.FC<NavigationHeaderProps> = ({
  activeTab,
  onTabChange,
  repaymentStatus,
  setRepaymentStatus,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const role = useSelector((state: RootState) => state.auth.role);

  const allNavItems = [
    { id: "customers", label: "Customer Management", icon: Users },
    { id: "quotations", label: "Quotations", icon: FileText },
    { id: "Sales-rep", label: "Sales Representatives", icon: Users },
    { id: "commissions", label: "Commissions", icon: HandCoins },
    { id: "rePayments", label: "Repayments", icon: RotateCcw },
    { id: "payments", label: "Payments", icon: ReceiptText },
  ];

  const navItems = allNavItems.filter((item) => {
    if (
      role === "account" ||
      role === "dispatch-lv1" ||
      role === "dispatch-lv2"
    ) {
      return item.id === "customers";
    }

    if (role === "Commission Dealer" || role === "Channel Partner") {
      if (item.id === "Sales-rep") return false;
    }

    if (role === "Commission Dealer") {
      return item.id !== "rePayments";
    }

    if (role === "Channel Partner") {
      return item.id !== "commissions";
    }

    return true; // others see all
  });

  const handleTabChange = (id: string) => {
    onTabChange(id as ICseDashboardTab);
    setMobileOpen(false);
  };

  return (
    <div className="border-b border-gray-100">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between px-4 sm:px-6 py-3 sm:py-4">
        {/* Navigation Tabs */}
        <div className="hidden sm:flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 w-full">
          <Tab
            tabs={navItems}
            active={activeTab}
            setActiveTab={(id: string) => onTabChange(id as ICseDashboardTab)}
          />

          {activeTab === ICseDashboardTab.RePayments && setRepaymentStatus && (
            <div className="flex items-center lg:ml-4">
              <div className="flex items-center bg-neutral-100/60 backdrop-blur-sm rounded-xl p-1 border border-neutral-200/40">
                {[
                  { value: IStatusCycle.PENDING, label: "Pending" },
                  { value: IStatusCycle.APPROVED, label: "Approved" },
                  { value: IStatusCycle.REJECTED, label: "Rejected" },
                ].map((option) => (
                  <motion.button
                    key={option.value}
                    onClick={() => setRepaymentStatus(option.value)}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium ${
                      repaymentStatus === option.value
                        ? "bg-white text-neutral-800 shadow-sm"
                        : "text-neutral-600"
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>{option.label}</span>
                  </motion.button>
                ))}
              </div>
            </div>
          )}
        </div>

        <button
          className="sm:hidden w-full p-2 rounded-md border border-gray-200"
          onClick={() => setMobileOpen((prev: boolean) => !prev)}
        >
          {mobileOpen ? <>Close</> : <>EPC Navigation</>}
        </button>
      </div>
      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="sm:hidden overflow-hidden bg-white border-t border-gray-100"
          >
            <div className="flex flex-col">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleTabChange(item.id)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm border-b
                      ${
                        activeTab === item.id
                          ? "bg-blue-50 text-blue-600 font-medium"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NavigationHeaderEPC;
